import { formatLiczby } from '@/lib/i18nNumber';
import { etykietaCenyZ, formatRange } from '@/lib/serpFormat';
import { etykietaWyboruPokoi } from '@/lib/pokoje';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import { etykietaObszaru } from '@/lib/areaSearch';
import type { SourcePostFilters } from '@/lib/serpRead/types';
import type { TFunction } from 'i18next';

/**
 * WIERSZE PANELU FILTRÓW (plan §1.19, §1.25, E3.1; makieta `docs/wireframes/serp-filter-panel.html`).
 *
 * Kolejność: najpierw sekcje, które USUWAJĄ oferty z listy (lokalizacja, cena, pokoje, metraż,
 * okolica, ogłoszenie, kaucja), potem kreska „Filtry poniżej porządkują wyniki, nie zawężają
 * ich.”, potem trzy, które tylko PORZĄDKUJĄ (mieszkanie, budynek, udogodnienia), na końcu
 * ramka diagnostyczna. Kaucja siedzi w pierwszej grupie, bo znana wyższa kaucja wypada.
 *
 * `budynek` niesie typ (A3) i wiek budynku (A4).
 */
export const SEKCJE_PANELU = [
  'lokalizacja',
  'cena',
  'pokoje',
  'metraz',
  'okolica',
  'ogloszenie',
  'kaucja',
  'mieszkanie',
  'budynek',
  'udogodnienia',
  'debug',
] as const;
export type SekcjaPanelu = (typeof SEKCJE_PANELU)[number];

/** Pierwsza sekcja MIĘKKA — przed nią stoi kreska z napisem. */
export const PIERWSZA_MIEKKA: SekcjaPanelu = 'mieszkanie';

/**
 * Domyślnie każdy wiersz jest otwarty poza Lokalizacją (jej podsumowanie już mówi, gdzie
 * szukasz) i ramką diagnostyczną (§1.19).
 */
export function domyslnieOtwarta(id: SekcjaPanelu): boolean {
  return id !== 'lokalizacja' && id !== 'debug';
}

const KLUCZ_PAMIECI = 'fh.filtry.sekcje';

/**
 * Stan zwinięcia pamiętany NA SESJĘ przeglądarki (makieta: „Open/closed state is remembered
 * per browser for the session. Defaults come back on a new visit.”). Wartość nieczytelna albo
 * brak storage (tryb prywatny) = domyślne.
 */
export function wczytajOtwarte(storage: Pick<Storage, 'getItem'> | null | undefined): Partial<Record<SekcjaPanelu, boolean>> {
  try {
    const surowe = storage?.getItem(KLUCZ_PAMIECI);
    if (!surowe) return {};
    const dane: unknown = JSON.parse(surowe);
    if (!dane || typeof dane !== 'object' || Array.isArray(dane)) return {};
    const wynik: Partial<Record<SekcjaPanelu, boolean>> = {};
    for (const id of SEKCJE_PANELU) {
      const v = (dane as Record<string, unknown>)[id];
      if (typeof v === 'boolean') wynik[id] = v;
    }
    return wynik;
  } catch {
    return {};
  }
}

export function zapiszOtwarte(storage: Pick<Storage, 'setItem'> | null | undefined, stan: Partial<Record<SekcjaPanelu, boolean>>): void {
  try {
    storage?.setItem(KLUCZ_PAMIECI, JSON.stringify(stan));
  } catch {
    /* tryb prywatny / pełny storage — stan żyje w pamięci komponentu */
  }
}

/** „A · B · C · +N” — najwyżej `maks` pozycji, reszta liczbą. */
export function zlaczDo(czesci: string[], maks: number): string {
  if (czesci.length <= maks) return czesci.join(' · ');
  return `${czesci.slice(0, maks).join(' · ')} · +${czesci.length - maks}`;
}

export interface PodsumowanieSekcji {
  /** Tekst po prawej stronie zwiniętego wiersza; pusty = nic nie ustawiono. */
  tekst: string;
  /** Czy to USTAWIONY filtr (zielony), czy tylko stan domyślny (szary, dziś tylko Lokalizacja). */
  ustawione: boolean;
}

const PUSTE: PodsumowanieSekcji = { tekst: '', ustawione: false };

/**
 * Wartość na zwiniętym wierszu (makieta, „Collapsed summary when set”). Te same etykiety, co
 * chipy nad listą — jedna prawda o filtrze w dwóch miejscach. Brak wartości = pusta prawa
 * strona, nie „Dowolne” (makieta, reguła 4); wyjątkiem jest Lokalizacja, która zwinięta mówi
 * „Cała Warszawa” — po to startuje zwinięta (reguła 2).
 */
export function podsumowanieSekcji(id: SekcjaPanelu, f: SourcePostFilters, t: TFunction, jezyk: string): PodsumowanieSekcji {
  const zlacz = (czesci: string[], maks: number): PodsumowanieSekcji =>
    czesci.length ? { tekst: zlaczDo(czesci, maks), ustawione: true } : PUSTE;

  switch (id) {
    case 'lokalizacja': {
      const nazwy = (f.areas ?? []).map((a) => etykietaObszaru(a, t));
      if (!nazwy.length) {
        const miasto = t(`search.area.miasto_mianownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY });
        return { tekst: t('search.area.whole_city', { miasto }), ustawione: false };
      }
      return { tekst: nazwy.length <= 3 ? nazwy.join(', ') : `${nazwy.slice(0, 3).join(', ')}, +${nazwy.length - 3}`, ustawione: true };
    }
    case 'cena': {
      const e = etykietaCenyZ(f, t, jezyk);
      return e ? { tekst: [e.zakres, e.dopisek].filter(Boolean).join(' '), ustawione: true } : PUSTE;
    }
    case 'pokoje': {
      const e = etykietaWyboruPokoi(f.rooms, t);
      return e ? { tekst: e, ustawione: true } : PUSTE;
    }
    case 'metraz': {
      const e = formatRange(t, f.areaMin, f.areaMax, 'm²', jezyk);
      return e ? { tekst: e, ustawione: true } : PUSTE;
    }
    case 'okolica': {
      const czesci: string[] = [];
      if (f.nearMetro) czesci.push(t('search.filters.summary.metro'));
      for (const p of ['transit', 'walk', 'green'] as const) if (f.poi?.includes(p)) czesci.push(t(`search.filters.summary.poi_${p}`));
      // „blisko: II LO im. Stefana Bat…” (makieta): nazwa przycięta, żeby podsumowanie zmieściło się w wierszu.
      for (const a of f.areas ?? []) {
        if (a.kind === 'place') czesci.push(t('search.filters.summary.place', { name: a.label.length > 26 ? `${a.label.slice(0, 24)}…` : a.label }));
      }
      return zlacz(czesci, 3);
    }
    case 'ogloszenie': {
      const czesci: string[] = [];
      if (f.wlascicielNaFlatHero) czesci.push(t('search.filters.owner_on_flathero'));
      if (f.ulubione) czesci.push(t('search.filters.favourites').toLocaleLowerCase(jezyk));
      if (f.availableNow) czesci.push(t('search.filters.available_now'));
      if (f.postedSinceHours) czesci.push(t(`search.filters.posted_options.${f.postedSinceHours}`).toLocaleLowerCase(jezyk));
      if (f.bezZdjec === false) czesci.push(t('search.filters.chip_only_with_photos').toLocaleLowerCase(jezyk));
      return zlacz(czesci, 3);
    }
    case 'kaucja':
      return f.kaucjaMax != null
        ? { tekst: t('search.filters.range_to', { v: `${formatLiczby(f.kaucjaMax, jezyk)} zł` }), ustawione: true }
        : PUSTE;
    case 'mieszkanie': {
      const czesci: string[] = [];
      if (f.umeblowanie) czesci.push(t(f.umeblowanie === 'umeblowane' ? 'search.filters.furnished_yes' : 'search.filters.furnished_no'));
      if (f.pietro) czesci.push(t('search.filters.floor_ground'));
      if (f.kuchnia) czesci.push(t(f.kuchnia === 'oddzielna' ? 'search.filters.chip_kitchen_separate' : 'search.filters.chip_kitchen_annex'));
      if (f.lazienki) czesci.push(t(f.lazienki === '1' ? 'search.filters.chip_baths_1' : 'search.filters.chip_baths_2plus'));
      if (f.internet) czesci.push(t('search.filters.internet_fibre'));
      if (f.balkon) czesci.push(t('search.filters.balcony'));
      if (f.winda) czesci.push(t('search.filters.elevator'));
      if (f.zeZwierzeciem) czesci.push(t('search.pets_chip'));
      return zlacz(czesci, 3);
    }
    case 'budynek': {
      const czesci: string[] = [];
      if (f.typBudynku) czesci.push(t(`search.filters.building_type_value.${f.typBudynku}`));
      if (f.wiekBudynku) czesci.push(t(`search.filters.building_age_short.${f.wiekBudynku}`));
      return zlacz(czesci, 2);
    }
    case 'udogodnienia': {
      const czesci = (f.udogodnienia ?? []).map((u) => t(`search.filters.amenity.${u}`));
      if (f.garaz) czesci.push(t('search.filters.chip_garage'));
      return zlacz(czesci, 2);
    }
    case 'debug':
      return { tekst: t('search.filters.debug_summary'), ustawione: false };
  }
}
